// This module is using axios: https://github.com/axios/axios
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import isAbortError from './async-request/is-abort-error';

const REQUEST_TIMEOUT = 60000; // default request timeout in ms, 1min

/*
 * Every helper here forwards its remaining fields straight into axios, so the
 * config is an AxiosRequestConfig with `url` promoted to required -- each helper
 * reads url positionally rather than leaving it on the config object.
 */
type RetryableRequestConfig = AxiosRequestConfig & { url: string };

const request = ({ url, ...rest }: RetryableRequestConfig) => {
  const config = {
    timeout: REQUEST_TIMEOUT,
    withCredentials: true,
    ...rest,
  };
  return axios.get(url, config);
};

/*
 * The return annotation is required, not decorative: these functions recurse, so
 * without it TypeScript cannot infer a return type and falls back to `any`
 * (TS7023), which then erases the response type at every call site.
 */
export const requestWithRetry = async (
  config: RetryableRequestConfig,
  n = 2,
): Promise<AxiosResponse> => {
  if (n < 1) throw new Error('Retry cannot be less than 1');
  try {
    return await request(config);
  } catch (err) {
    // A caller-cancelled request is not a failure; retrying it is pointless.
    if (n === 1 || isAbortError(err)) throw err;
    return requestWithRetry(config, n - 1);
  }
};

const putRequest = ({
  data,
  url,
  timeout,
  ...rest
}: RetryableRequestConfig) => {
  const config = {
    timeout: timeout || REQUEST_TIMEOUT,
    withCredentials: true,
    ...rest,
  };
  return axios.put(url, data, config);
};

export const putRequestWithRetry = async (
  config: RetryableRequestConfig,
  n = 2,
): Promise<AxiosResponse> => {
  if (n < 1) throw new Error('Retry cannot be less than 1');
  try {
    return await putRequest(config);
  } catch (err) {
    // A caller-cancelled request is not a failure; retrying it is pointless.
    if (n === 1 || isAbortError(err)) throw err;
    return putRequestWithRetry(config, n - 1);
  }
};

const postRequest = ({
  url,
  data,
  timeout,
  ...rest
}: RetryableRequestConfig) => {
  const config = {
    timeout: timeout || REQUEST_TIMEOUT,
    withCredentials: true,
    headers: {
      ['anti-csrftoken-a2z']: window.__AAC_FIT_CSRF_TOKEN__,
    },
    ...rest,
  };
  return axios.post(url, data, config);
};

export const postRequestWithRetry = async (
  config: RetryableRequestConfig,
  n = 2,
): Promise<AxiosResponse> => {
  if (n < 1) throw new Error('Retry cannot be less than 1');
  try {
    return await postRequest(config);
  } catch (err) {
    // A caller-cancelled request is not a failure; retrying it is pointless.
    if (n === 1 || isAbortError(err)) throw err;
    return postRequestWithRetry(config, n - 1);
  }
};
